import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Team } from '@lms/shared-types'
import { listTeams } from '../../api/teams'
import { createTeam } from '../../api/admin-teams'
import { ApiError } from '../../api/client'
import { Button } from './Button'

// ── Styles ────────────────────────────────────────────────────────────────────

const fieldStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  boxSizing: 'border-box',
}

const inlineCreateStyle: CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
}

const errorStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--danger-fg)',
  margin: '4px 0 0',
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface TeamSelectWithCreateProps {
  /** Currently selected team id, or '' for none. */
  value: string
  /** Called with the chosen team id whenever HR picks (or creates) a team. */
  onChange: (teamId: string) => void
  /** testId prefix; the native <select> keeps `${testId}` for parity with the
   *  plain select it replaces, child elements append `-create-input` etc. */
  testId: string
  /** Whether the control is required (sets the <select> required attr). */
  required?: boolean
  /** Optional extra teams to merge into the list, e.g. teams the parent
   *  already has in hand (avoids a second fetch). When omitted the picker
   *  fetches `/api/v1/teams` itself. */
  teams?: ReadonlyArray<{ id: string; name: string }>
  /** Placeholder option label. Defaults to "Pick a team…". */
  placeholder?: string
}

// ── Component ─────────────────────────────────────────────────────────────────

/** Sentinel value used as the <option> that flips the control into
 *  inline-create mode. A real team id is a UUID, so this can't collide. */
const CREATE_SENTINEL = '__create_new_team__'

/**
 * Team picker with inline "create a new team" support.
 *
 * Renders a native `<select>` of teams (so keyboard + viewport behaviour stays
 * familiar) plus a trailing "＋ Add new team…" option. Selecting that option
 * swaps the select for an inline create row (text input + Create / Cancel).
 * On create it POSTs to `/api/v1/admin/teams`, refreshes the teams query so
 * the new team is immediately selectable, and calls `onChange` with the new
 * id so the parent form lands the new hire on the freshly created team.
 *
 * testId conventions (data-testid):
 *   `${testId}`            — the native <select> (parity with the plain select)
 *   `${testId}-create-row` — the inline create row wrapper
 *   `${testId}-create-input` — the new-team name input
 *   `${testId}-create-submit` — the Create button
 *   `${testId}-create-cancel` — the Cancel button
 *   `${testId}-create-error` — inline error text (present only on failure)
 */
export function TeamSelectWithCreate({
  value,
  onChange,
  testId,
  required,
  teams: teamsProp,
  placeholder = 'Pick a team…',
}: Readonly<TeamSelectWithCreateProps>): JSX.Element {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState<boolean>(false)
  const [draftName, setDraftName] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  // Teams created inline during this picker's lifetime that aren't yet in the
  // parent's prop / the shared query cache. Kept so the freshly-created team
  // renders as the selected option immediately (the parent's `teams` prop
  // only refreshes when the org-chart query refetches, which is async).
  const [createdLocal, setCreatedLocal] = useState<
    ReadonlyArray<{ id: string; name: string }>
  >([])

  // Only fetch when the parent didn't hand us a teams list. listTeams returns
  // the full Team[] (with members + slotPct); we project to {id,name} for the
  // dropdown but keep the richer rows so the cache refresh keeps the picker
  // in sync with HR's other team surfaces.
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
    enabled: teamsProp == null,
    staleTime: 60_000,
  })

  const teams = useMemo(() => {
    const base: ReadonlyArray<{ id: string; name: string }> =
      teamsProp ?? teamsQuery.data ?? []
    // Merge in locally-created teams that haven't propagated to the source yet,
    // de-duplicated by id.
    const seen = new Set(base.map((t) => t.id))
    const merged = [...base, ...createdLocal.filter((t) => !seen.has(t.id))]
    return [...merged].sort((a, b) => a.name.localeCompare(b.name))
  }, [teamsProp, teamsQuery.data, createdLocal])

  const createMutation = useMutation({
    mutationFn: () => createTeam(draftName.trim()),
    onSuccess: async (created) => {
      // Track locally so the option renders before the cache refetch lands.
      setCreatedLocal((prev) =>
        prev.some((t) => t.id === created.id) ? prev : [...prev, created],
      )
      // Refresh every consumer of the teams list (this picker, the
      // coverage-thresholds page, the org chart's cache aliases) so the new
      // team is selectable everywhere without a manual reload.
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      setError(null)
      setDraftName('')
      setCreating(false)
      onChange(created.id)
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create team')
    },
  })

  function handleSelectChange(next: string): void {
    if (next === CREATE_SENTINEL) {
      setCreating(true)
      // Keep the current selection visible until HR confirms/cancels so the
      // field never looks blank mid-flow.
      return
    }
    onChange(next)
  }

  function handleCreateSubmit(): void {
    setError(null)
    if (draftName.trim().length === 0) {
      setError('Team name is required.')
      return
    }
    createMutation.mutate()
  }

  function handleCreateCancel(): void {
    setError(null)
    setDraftName('')
    setCreating(false)
  }

  if (creating) {
    return (
      <div data-testid={`${testId}-create-row`} style={inlineCreateStyle}>
        <input
          data-testid={`${testId}-create-input`}
          type="text"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleCreateSubmit()
            } else if (e.key === 'Escape') {
              handleCreateCancel()
            }
          }}
          autoFocus
          maxLength={120}
          placeholder="New team name"
          aria-label="New team name"
          style={fieldStyle}
        />
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={handleCreateSubmit}
          loading={createMutation.isPending}
          data-testid={`${testId}-create-submit`}
        >
          Create
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          type="button"
          onClick={handleCreateCancel}
          disabled={createMutation.isPending}
          data-testid={`${testId}-create-cancel`}
        >
          Cancel
        </Button>
        {error ? (
          <p data-testid={`${testId}-create-error`} style={errorStyle}>
            {error}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => handleSelectChange(e.target.value)}
      style={fieldStyle}
      required={required}
    >
      <option value="">{placeholder}</option>
      {teams.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
      <option value={CREATE_SENTINEL}>＋ Add new team…</option>
    </select>
  )
}

/**
 * Re-export the Team type alias used internally so callers that want to
 * pass a pre-fetched teams list can keep their imports tidy. Kept narrow
 * (id + name) since that's all the dropdown renders.
 */
export type TeamOption = Pick<Team, 'id' | 'name'>
